import {
  apiV1MyAccountAiCreditPacksGet,
  apiV1MyAccountAiCreditPacksPurchasePost,
  apiV1MyAccountAiCreditPacksPurchasesInvoiceIdQrGet,
  getAiCreditsApiV1MyAccountAiCreditsGet,
} from "@/backend-sdk/client/sdk.gen";
import type {
  AiCreditPackOffer,
  AiCreditPurchaseRequest,
  CheckoutPaymentResponse,
  MyAccountAiCreditsResponse,
  PixQrResponse,
} from "@/backend-sdk/client/types.gen";

/**
 * <M-280> A compra avulsa de créditos de IA.
 *
 * O saldo de IA é **por loja**: a franquia do plano, que a virada do ciclo recarrega, e o
 * comprado em pacote, que não expira. O chat e a auditoria gastam a franquia primeiro. A
 * compra é do dono da conta, para uma loja dele; os pacotes e os preços são definidos pelo
 * admin.
 */

/**
 * O `402` do chat e da auditoria: a loja ficou sem créditos de IA para o pedido (ou sem
 * assinatura que os dê). A mensagem é o `detail` do backend; o tipo é o que deixa a tela
 * oferecer a compra em vez de mostrar um erro qualquer.
 */
export class AiCreditsExhaustedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AiCreditsExhaustedError";
  }
}

/** O status do `402` — o único que o backend usa para crédito de IA insuficiente. */
export const STATUS_SEM_CREDITOS = 402;

/** Os pacotes à venda, na ordem que o admin definiu. */
export const listAiCreditPacks = async (): Promise<AiCreditPackOffer[]> => {
  const { data } = await apiV1MyAccountAiCreditPacksGet({
    throwOnError: true,
  });
  return data.packs;
};

export const purchaseAiCreditPack = async (
  body: AiCreditPurchaseRequest,
): Promise<CheckoutPaymentResponse> => {
  const { data } = await apiV1MyAccountAiCreditPacksPurchasePost({
    body,
    throwOnError: true,
  });
  return data;
};

/** O QR do PIX de uma compra aberta — a rota nunca falha por causa do gateway. */
export const fetchAiCreditPurchaseQr = async (
  invoiceId: string,
): Promise<PixQrResponse> => {
  const { data } = await apiV1MyAccountAiCreditPacksPurchasesInvoiceIdQrGet({
    path: { invoice_id: invoiceId },
    throwOnError: true,
  });
  return data;
};

/** O saldo de uma loja, nas três partes que a tela mostra. */
export interface SaldoDeIa {
  readonly unlimited: boolean;
  /** Franquia mais comprado — o que a loja pode gastar. `null` é ilimitado. */
  readonly total: number | null;
  /** O que resta da franquia do ciclo. */
  readonly franquia: number | null;
  /** A franquia inteira do plano no ciclo. */
  readonly franquiaDoCiclo: number | null;
  /** O comprado em pacotes, que não expira. */
  readonly comprado: number;
}

/**
 * `total_balance` e `purchased_balance` chegaram com M-280; sem eles (API anterior), o total é
 * a franquia e o comprado é zero — que era a verdade antes de haver compra.
 */
export const paraSaldoDeIa = (dados: MyAccountAiCreditsResponse): SaldoDeIa => {
  const franquia = dados.balance ?? null;
  const comprado = dados.purchased_balance ?? 0;
  return {
    unlimited: dados.unlimited,
    total: dados.unlimited
      ? null
      : (dados.total_balance ?? (franquia === null ? null : franquia + comprado)),
    franquia,
    franquiaDoCiclo: dados.granted ?? null,
    comprado,
  };
};

export const fetchSaldoDeIa = async (customerUuid: string): Promise<SaldoDeIa> => {
  const { data } = await getAiCreditsApiV1MyAccountAiCreditsGet({
    query: { customer_uuid: customerUuid },
    throwOnError: true,
  });
  return paraSaldoDeIa(data);
};
